import {
    ChangeEvent,
    FC,
    useEffect,
    useMemo,
    useState,
} from 'react'
import { useToggle } from 'react-use'
import {
    Col,
    FormGroup,
    Input,
    Label,
    ModalBody,
    Row,
} from 'reactstrap'

import { PacoLogsModal, WaitingDayHoursInput } from '../../../../@paco/compositions'
import { AbsenceFormData } from '../../../../@paco/entities/Absence/Absence'
import { PayrollPeriod } from '../../../../@paco/entities/PayrollPeriod/PayrollPeriod'
import { doesPeriodOverlapWithLockedPayrollPeriod } from '../../../../@paco/entities/PayrollPeriod/PayrollPeriodHelpers'
import { transformToPeriod } from '../../../../@paco/entities/Period/PeriodTransformers'
import { Permission } from '../../../../@paco/entities/Permission/Permission'
import { Role } from '../../../../@paco/entities/Role/Role'
import { transformLegacyRoleToRole } from '../../../../@paco/entities/Role/RoleTransformers'
import useCheckPermission from '../../../../@paco/helpers/hooks/useCheckPermission'
import { getEventLogsByEntityUuid } from '../../../../@paco/redux/eventLogs/eventLogsActions'

import FormDatePicker from '../../../../components/FormDatePicker/FormDatePicker'
import FormFooter from '../../../../components/forms/FormFooter/FormFooter'
import LeaveOfAbsenceInfo from '../../../../components/LeaveOfAbsenceRequestCard/LeaveOfAbsenceInfo'
import LegacyHoursWarning from '../../../../components/LegacyHoursWarning/LegacyHoursWarning'
import ModalHeader from '../../../../components/ModelHeader/ModalHeader'
import WeekdayHoursInputs from '../../../../components/WeekHoursInputs/WeekHoursInputs'
import { formatDate } from '../../../../helpers/date'
import useUserHasWaitingDayHours from '../../../../helpers/hooks/useUserHasWaitingDayHours'
import { getPermissionToAdd, getPermissionToDelete, getPermissionToEdit } from '../../../../helpers/permissions/getPermissionToAction'
import { translate } from '../../../../helpers/translations/translator'
import { Absence, PayrollPeriodViewModel, WeekWithHours } from '../../../../models'
import { useTypedDispatch, useTypedSelector } from '../../../../redux/store'
import transformAbsenceHoursToWaitingDayHours from '../../../../services/AbsenceHoursService/transformAbsenceHoursToWaitingDayHours'
import { isStartDateLaterThanEndDate } from '../../helpers'
import getAbsencePayrollPeriodEndDate from './helpers/getAbsencePayrollPeriodEndDate'
import { isAllWeeksLocked } from './helpers/isAllWeeksLocked'
import { transformAbsenceHoursToWeekDayWithHours } from './helpers/transformAbsenceHoursToWeekDayWithHours'

interface EditAbsenceFormProps {
    currentUserRole?: Role;
    data: Absence;
    pacoPayrollPeriods: PayrollPeriod[];
    payrollPeriods: PayrollPeriodViewModel[];
    permissions: Permission[];
    onCancel: () => void;
    onSubmit: (
        absence: Absence,
        data: AbsenceFormData,
        weeksWithHours: WeekWithHours[],
    ) => void;
    onDelete?: (absence: Absence) => void;
}

const dateFormat = 'yyyy-MM-dd'
const timeFormat = 'HH:mm'

const EditAbsenceForm: FC<EditAbsenceFormProps> = ({
    currentUserRole,
    data,
    pacoPayrollPeriods,
    payrollPeriods,
    permissions = [],
    onCancel,
    onSubmit,
    onDelete,
}) => {
    const dispatch = useTypedDispatch()

    const { eventLogsByEntity, isLoading: logsLoading } = useTypedSelector(state => state.eventLogsReducer)

    const [startDate, setStartDate] = useState<string | null>('')
    const [startTime, setStartTime] = useState('')
    const [endDate, setEndDate] = useState<string | null>('')
    const [endTime, setEndTime] = useState('')
    const [waitingDayHours, setWaitingDayHours] = useState<number | null>(null)
    const [weeksWithHours, setWeekdaysWithHours] = useState<WeekWithHours[]>([])
    const [legacyWeeksWithHours, setLegacyWeeksWithHours] = useState<WeekWithHours[]>([])
    const [showHistory, toggleShowHistory] = useToggle(false)

    const start = useMemo(() => (
        startDate ? `${formatDate(startDate, 'yyyy-MM-dd')}T${startTime}:00` : ''
    ), [startDate, startTime])
    const absencesUserRoles = useMemo(() => data.user.roles.map(transformLegacyRoleToRole), [data])
    const canEditAbsence = useMemo(() => getPermissionToEdit(currentUserRole, absencesUserRoles, permissions, 'absences'), [])
    const canDeleteAbsence = useMemo(() => getPermissionToDelete(currentUserRole, absencesUserRoles, permissions, 'absences'), [])
    const canAddAbsenceHours = useMemo(() => getPermissionToAdd(currentUserRole, absencesUserRoles, permissions, 'absence-hours'), [])
    const canEditAbsenceHours = useMemo(() => getPermissionToEdit(currentUserRole, absencesUserRoles, permissions, 'absence-hours'), [])
    const canDeleteAbsenceHours = useMemo(() => getPermissionToDelete(currentUserRole, absencesUserRoles, permissions, 'absence-hours'), [])
    const canViewHistory = useCheckPermission('view-all-logs')

    const canModifyAbsenceHours = canEditAbsenceHours && canDeleteAbsenceHours && canAddAbsenceHours
    const isPeriodLocked = isAllWeeksLocked(weeksWithHours, startDate)

    const [userHasWaitingDayHours, waitingDayHoursLoading] = useUserHasWaitingDayHours(data, start)

    const dateError = isStartDateLaterThanEndDate(startDate, startTime, endDate, endTime)
    const payrollPeriodsStartDate = (startDate && startTime) ? `${startDate}T${startTime}` : null
    const payrollPeriodsEndDate = useMemo(() => getAbsencePayrollPeriodEndDate(payrollPeriodsStartDate, endDate, startTime), [payrollPeriodsStartDate, endDate, startTime])

    const validEndDate = (! endDate && ! endTime) || (endDate && endTime.length)
    const canSubmit = !! (startDate && startTime.length && validEndDate && ! dateError)

    const payrollPeriodError = useMemo(() => {
        if (! payrollPeriodsStartDate || ! payrollPeriodsEndDate) {
            return false
        }

        return doesPeriodOverlapWithLockedPayrollPeriod(
            transformToPeriod(new Date(payrollPeriodsStartDate), new Date(payrollPeriodsEndDate.date)),
            pacoPayrollPeriods,
        )
    }, [startDate, endDate, payrollPeriods])

    useEffect(() => {
        if (data) {
            const weekdays = transformAbsenceHoursToWeekDayWithHours(
                data.absenceHours || [],
                pacoPayrollPeriods,
            )

            setWeekdaysWithHours(weekdays)
            setLegacyWeeksWithHours(weekdays)
            setStartDate(formatDate(data.start, dateFormat))
            setStartTime(formatDate(data.start, timeFormat))
            setEndDate(data.end ? formatDate(data.end, dateFormat) : null)
            setEndTime(data.end ? formatDate(data.end, timeFormat) : '')
            setWaitingDayHours(transformAbsenceHoursToWaitingDayHours(data.absenceHours))
        }
    }, [data])

    const onFormSubmit = (e: ChangeEvent<HTMLFormElement>) => {
        e.preventDefault()

        const end = endDate ? `${formatDate(endDate, 'yyyy-MM-dd')} ${endTime}:00` : null

        onSubmit(
            data,
            {
                start: new Date(start),
                end: end ? new Date(end) : undefined,
                waitingDayHours: waitingDayHours || 0,
            },
            canModifyAbsenceHours ? weeksWithHours : [],
        )
    }

    const onChangeStartTime = (e: ChangeEvent<HTMLInputElement>) => {
        setStartTime(e.target.value)
    }

    const onChangeStartDate = (date: Date) => {
        setStartDate(date ? formatDate(date, dateFormat) : null)
    }

    const onChangeEndTime = (e: ChangeEvent<HTMLInputElement>) => {
        setEndTime(e.target.value)
    }

    const onChangeEndDate = (date: Date) => {
        setEndDate(date ? formatDate(date, dateFormat) : null)
    }

    const onChangeWaitingDayHours = (value: number | undefined) => {
        setWaitingDayHours(value === undefined ? null : value)
    }

    const onDeleteClick = () => {
        if (onDelete) onDelete(data)
    }

    const dispatchOpenLogsModal = (id: string) => {
        dispatch(getEventLogsByEntityUuid(id))
        toggleShowHistory(true)
    }

    const dispatchCloseLogsModal = () => {
        toggleShowHistory(false)
    }

    const onWeekdayHoursInputsChange = (weekdays: WeekWithHours[]) => {
        setWeekdaysWithHours(weekdays)
    }

    if (! data) {
        return null
    }

    return (
        <form onSubmit={onFormSubmit}>
            <ModalHeader
                showHistoryButton={canViewHistory}
                title={translate('pages.absences.editAbsence')}
                isLoading={waitingDayHoursLoading}
                onHistoryButtonClick={() => dispatchOpenLogsModal(data.id)}
            />
            <ModalBody>
                <Row>
                    <Col>
                        <LeaveOfAbsenceInfo
                            absence={data}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormGroup>
                            <Label>{translate('pages.tracks.startDate')}</Label>
                            <FormDatePicker
                                id="startDate"
                                selected={startDate}
                                onChange={onChangeStartDate}
                            />
                        </FormGroup>
                    </Col>
                    <Col>
                        <FormGroup>
                            <Label>{translate('pages.tracks.startTime')}</Label>
                            <Input
                                type="time"
                                className="form-control"
                                id="startTime"
                                name="startTime"
                                autoComplete="off"
                                value={startTime}
                                onChange={onChangeStartTime}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <FormGroup>
                            <Label>{translate('pages.tracks.endDate')}</Label>
                            <FormDatePicker
                                id="endDate"
                                selected={endDate}
                                onChange={onChangeEndDate}
                                invalid={dateError}
                                feedback={translate('pages.management.endDateNeedsToBeLaterThanStartDate')}
                            />
                        </FormGroup>
                    </Col>
                    <Col>
                        <FormGroup>
                            <Label>{translate('pages.tracks.endTime')}</Label>
                            <Input
                                type="time"
                                className="form-control"
                                id="endTime"
                                name="endTime"
                                autoComplete="off"
                                value={endTime || ''}
                                onChange={onChangeEndTime}
                            />
                        </FormGroup>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <LegacyHoursWarning
                            weekWithHours={legacyWeeksWithHours}
                            type="absences"
                        />
                    </Col>
                </Row>
                {userHasWaitingDayHours && (
                    <Row>
                        <Col>
                            <WaitingDayHoursInput
                                isDisabled={! canModifyAbsenceHours || isPeriodLocked}
                                value={waitingDayHours === null ? undefined : waitingDayHours}
                                onChange={onChangeWaitingDayHours}
                            />
                        </Col>
                        <Col />
                    </Row>
                )}
                <Row>
                    <Col>
                        <WeekdayHoursInputs
                            disabled={! canModifyAbsenceHours}
                            pacoPayrollPeriods={pacoPayrollPeriods}
                            payrollPeriods={payrollPeriods}
                            weekWithHours={weeksWithHours}
                            type="absences"
                            startDate={
                                payrollPeriodsStartDate ? new Date(payrollPeriodsStartDate) : null
                            }
                            endDate={payrollPeriodsEndDate}
                            onChange={onWeekdayHoursInputsChange}
                        />
                    </Col>
                </Row>
            </ModalBody>
            <FormFooter
                isDisabled={! canEditAbsence}
                canSubmit={canSubmit}
                onCancel={onCancel}
                onDelete={canDeleteAbsence ? onDeleteClick : undefined}
                deleteButtonError={payrollPeriodError ? translate('common.datesOverlapWithLockedPayrollPeriod') : undefined}
            />

            <PacoLogsModal
                isLoading={logsLoading}
                isOpen={showHistory && ! logsLoading}
                title={translate('pages.absences.absenceLog')}
                emptyMessage={translate('pages.absences.emptyAbsenceLog')}
                logs={eventLogsByEntity}
                onClose={dispatchCloseLogsModal}
            />
        </form>
    )
}

export default EditAbsenceForm
